import React from 'react';

import { CloseMode, ViewMode } from './constants';

export interface NavigationContextValue {
    /** The view mode of the parent navigation, either 'horizontal' or 'vertical'. */
    viewMode: ViewMode;
    /** How a closed section or popover of the parent navigation renders. */
    closeMode: CloseMode;
}

const DEFAULT_NAVIGATION_CONTEXT: NavigationContextValue = {
    viewMode: ViewMode.vertical,
    closeMode: 'unmount',
};

/**
 * Shares the options of the parent `Navigation` with every descendant.
 *
 * `Navigation` provides the value. `NavigationItem`, `NavigationSection` and
 * `NavigationSectionLink` read it. The default is the vertical view mode with the 'unmount' close
 * mode.
 */
export const NavigationContext = React.createContext<NavigationContextValue>(DEFAULT_NAVIGATION_CONTEXT);

/** Return the options of the closest parent navigation. */
export function useNavigationContext(): NavigationContextValue {
    return React.useContext(NavigationContext);
}

/**
 * Build a context value that keeps the options of the parent navigation.
 * The value overrides the view mode only.
 */
export function useNestedNavigationContext(viewMode: ViewMode): NavigationContextValue {
    const parentContext = useNavigationContext();

    return React.useMemo(() => ({ ...parentContext, viewMode }), [parentContext, viewMode]);
}
